import { Badge, SegmentedControl } from '@mogzo/ui';
import {
  Castle,
  CircleCheck,
  Database,
  type LucideIcon,
  Radio,
  Settings,
  Swords,
  Trophy,
  Users,
} from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Achievements } from '../components/Achievements';
import { Attacks } from '../components/Attacks';
import { Leaderboards } from '../components/Leaderboards';
import { Matches } from '../components/Matches';
import { useProject } from '../components/ProjectLayout';
import { Rooms } from '../components/Rooms';
import { Alert, Button, Chip, cardClass, ErrorNotice, fieldClass, Loading } from '../components/ui';
import { ApiError, apiSend, type Project } from '../lib/api';
import { initials, timeAgo } from '../lib/format';
import { useApi } from '../lib/use-api';

type Env = 'test' | 'prod';
type Tab =
  | 'saves'
  | 'data'
  | 'leaderboards'
  | 'achievements'
  | 'config'
  | 'matches'
  | 'attacks'
  | 'rooms';

interface Summary {
  storageBytes: number;
  storageLimit: number;
  playersWithSaves: number;
  documents: number;
  collections: { name: string; scope: 'game' | 'player'; documents: number }[];
}
interface PlayerRef {
  playerId: string;
  name: string | null;
}
interface PlayerRow extends PlayerRef {
  saves: number;
  changedAt: string;
}
interface SaveRow {
  saveId: number;
  key: string;
  data: unknown;
  bytes: number;
  updatedAt: string;
}
interface DocumentRow {
  documentId: number;
  id: string;
  scope: 'game' | 'player';
  player: PlayerRef | null;
  bytes: number;
  updatedAt: string;
}
interface FullDocument extends DocumentRow {
  collection: string;
  data: unknown;
}
interface ConfigState {
  config: Record<string, unknown>;
  bytes: number;
  limit: number;
  updatedAt: string | null;
}

function failure(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Could not reach the API. Try again.';
}

/** Bytes as people read them: `31 bytes`, `0.4 KB`, `41.8 MB`, `200 MB`. */
function size(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Number((bytes / 1024).toFixed(1))} KB`;
  return `${Number((bytes / 1024 / 1024).toFixed(1))} MB`;
}

/** JSON as typed, or the reason it isn't. */
function parseJson(text: string): { ok: true; value: unknown } | { ok: false; message: string } {
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch (error) {
    return { ok: false, message: `That isn’t valid JSON: ${(error as Error).message}` };
  }
}

const pretty = (value: unknown) => JSON.stringify(value, null, 2);
const linkClass =
  'font-semibold underline decoration-brand decoration-2 underline-offset-4 hover:decoration-ink';
const monoArea = `${fieldClass} font-mono text-[13px] leading-relaxed`;

function Card({
  label,
  title,
  aside,
  children,
  className = '',
}: {
  label: string;
  title: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      aria-labelledby={label}
      className={`${cardClass} flex flex-col gap-3 p-5 ${className}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id={label} className="text-[15px] font-semibold">
          {title}
        </h2>
        {aside && <span className="text-[13px] text-muted">{aside}</span>}
      </div>
      {children}
    </section>
  );
}

/**
 * A JSON value: editable (test data, config) or shown read-only (live data). Saving parses
 * first, so a typo never reaches the game.
 */
function JsonEditor({
  label,
  value,
  readOnly,
  rows = 8,
  note,
  onSave,
  saveLabel = 'Save changes',
  onDelete,
  deleteLabel,
}: {
  label: string;
  value: unknown;
  readOnly: boolean;
  rows?: number;
  note?: ReactNode;
  onSave?: (value: unknown) => Promise<void>;
  saveLabel?: string;
  onDelete?: () => Promise<void>;
  deleteLabel?: string;
}) {
  const [draft, setDraft] = useState(pretty(value));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Follows the value when it reloads (after a save, or a change elsewhere). Each item gets
  // its own editor (a `key`), so "Saved." never carries over to another one. Only a real change
  // counts: this effect runs a moment after the editor appears, and copying the same value
  // again then would wipe out anything typed in that moment.
  const text = pretty(value);
  const shown = useRef(text);
  useEffect(() => {
    if (text === shown.current) return;
    shown.current = text;
    setDraft(text);
    setError(null);
    setConfirming(false);
  }, [text]);

  if (readOnly) {
    return (
      <>
        <pre className="overflow-x-auto rounded-[6px] border border-line bg-field px-4 py-3 font-mono text-[13px] leading-relaxed whitespace-pre-wrap">
          {pretty(value)}
        </pre>
        {note && <p className="text-sm text-muted">{note}</p>}
      </>
    );
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    const parsed = parseJson(draft);
    if (!parsed.ok) return setError(parsed.message);
    setBusy(true);
    setError(null);
    try {
      await onSave?.(parsed.value);
      setSaved(true);
    } catch (failed) {
      setError(failure(failed));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await onDelete?.();
    } catch (failed) {
      setError(failure(failed));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={save} className="flex flex-col gap-3">
      {error && <Alert tone="error">{error}</Alert>}
      {saved && <Alert tone="success">Saved.</Alert>}
      <label className="sr-only" htmlFor={`${label}-json`}>
        {label}
      </label>
      <textarea
        id={`${label}-json`}
        rows={rows}
        value={draft}
        spellCheck={false}
        onChange={(event) => {
          setDraft(event.target.value);
          setSaved(false);
        }}
        className={monoArea}
      />
      {note && <p className="text-sm text-muted">{note}</p>}
      <div className="flex flex-wrap items-center gap-2.5">
        <Button type="submit" disabled={busy}>
          {busy ? 'Saving…' : saveLabel}
        </Button>
        <Button
          variant="secondary"
          disabled={busy}
          onClick={() => {
            setDraft(pretty(value));
            setError(null);
          }}
        >
          Cancel
        </Button>
        {onDelete &&
          (confirming ? (
            <span className="ml-auto flex flex-wrap items-center gap-2 rounded-[6px] bg-danger-soft px-3 py-2">
              <span className="text-sm font-semibold text-danger">Delete it?</span>
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                className="mz-btn mz-btn-danger"
              >
                Yes, delete
              </button>
              <Button variant="secondary" onClick={() => setConfirming(false)} disabled={busy}>
                Keep it
              </Button>
            </span>
          ) : (
            <Button variant="danger" className="ml-auto" onClick={() => setConfirming(true)}>
              {deleteLabel}
            </Button>
          ))}
      </div>
    </form>
  );
}

function PlayerName({ player }: { player: PlayerRef }) {
  return (
    <span className="flex min-w-0 flex-col">
      {player.name && <strong className="truncate">{player.name}</strong>}
      <span className={`truncate font-mono text-[13px] ${player.name ? 'text-muted' : ''}`}>
        {player.name ? player.playerId : `Player ${player.playerId}`}
      </span>
    </span>
  );
}

// --- Saves -------------------------------------------------------------------------------

function Saves({
  project,
  env,
  refresh,
  onChanged,
}: {
  project: Project;
  env: Env;
  /** Bumped by the page (after clearing test data) to reload the lists. */
  refresh: number;
  onChanged: () => void;
}) {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const base = `/v1/dev/projects/${project.id}/data`;
  const players = useApi<{ players: PlayerRow[] }>(
    `${base}/players?env=${env}${query ? `&search=${encodeURIComponent(query)}` : ''}`,
    reload + refresh,
  );
  const saves = useApi<{ player: PlayerRef; saves: SaveRow[] }>(
    selected ? `${base}/players/${encodeURIComponent(selected)}/saves?env=${env}` : null,
    reload + refresh,
  );

  useEffect(() => {
    if (players.status === 'ready' && !selected) {
      setSelected(players.data.players[0]?.playerId ?? null);
    }
  }, [players, selected]);

  const changed = () => {
    setReload((n) => n + 1);
    onChanged();
  };
  const list = saves.status === 'ready' && selected ? saves.data.saves : [];
  const open = list.find((save) => save.key === openKey) ?? list[0] ?? null;

  return (
    <div className="flex flex-wrap items-start gap-5">
      <Card label="players-heading" title="Players" className="max-w-[380px] flex-[1_1_280px]">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setQuery(search.trim());
            setSelected(null);
          }}
        >
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-muted">
            {env === 'test' ? 'Find a player' : 'Find a player id'}
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={env === 'test' ? 'Name or player id' : 'Player id'}
              className={fieldClass}
            />
          </label>
        </form>
        {env === 'prod' && (
          <p className="text-[13px] text-muted">
            A player can tell you their id from your game, for support.
          </p>
        )}
        {players.status === 'loading' && <Loading />}
        {players.status === 'error' && <ErrorNotice error={players.error} />}
        {players.status === 'ready' &&
          (players.data.players.length === 0 ? (
            <p className="py-4 text-sm text-muted">
              {query
                ? 'No player matches.'
                : 'No saves yet. They appear here once your game saves.'}
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {players.data.players.map((player) => (
                <li key={player.playerId}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(player.playerId);
                      setOpenKey(null);
                    }}
                    aria-pressed={player.playerId === selected}
                    className={`flex w-full items-center gap-3 rounded-[6px] border px-3 py-2.5 text-left ${
                      player.playerId === selected
                        ? 'border-brand bg-cream'
                        : 'border-transparent hover:border-line'
                    }`}
                  >
                    {player.name && (
                      <span
                        aria-hidden="true"
                        className="flex size-9 shrink-0 items-center justify-center rounded-full bg-avatar text-xs font-semibold"
                      >
                        {initials(player.name)}
                      </span>
                    )}
                    <PlayerName player={player} />
                    <span className="ml-auto shrink-0 text-right text-[13px] text-muted">
                      {player.saves} {player.saves === 1 ? 'save' : 'saves'}
                      <br />
                      {timeAgo(player.changedAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ))}
      </Card>

      <div className="flex min-w-0 flex-[999_1_420px] flex-col gap-5">
        {saves.status === 'error' && <ErrorNotice error={saves.error} />}
        {saves.status === 'ready' && selected && (
          <>
            <Card
              label="saves-heading"
              title={`${saves.data.player.name ?? `Player ${saves.data.player.playerId}`}’s saves`}
              aside={`${list.length} · ${size(list.reduce((total, save) => total + save.bytes, 0))}`}
            >
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="text-left text-xs text-muted">
                      <th className="border-b border-divider px-3 py-2.5">Name</th>
                      <th className="border-b border-divider px-3 py-2.5">Size</th>
                      <th className="border-b border-divider px-3 py-2.5">Changed</th>
                      <th className="border-b border-divider px-3 py-2.5">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((save) => (
                      <tr key={save.saveId} className={save.key === open?.key ? 'bg-cream' : ''}>
                        <td className="border-b border-divider px-3 py-3 font-mono text-[13px]">
                          {save.key}
                        </td>
                        <td className="border-b border-divider px-3 py-3 text-muted">
                          {size(save.bytes)}
                        </td>
                        <td className="border-b border-divider px-3 py-3 text-muted">
                          {timeAgo(save.updatedAt)}
                        </td>
                        <td className="border-b border-divider px-3 py-3 text-right">
                          <button
                            type="button"
                            className={linkClass}
                            aria-label={`${env === 'test' ? 'Open' : 'View'} ${save.key}`}
                            onClick={() => setOpenKey(save.key)}
                          >
                            {env === 'test' ? 'Open' : 'View'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            {open && (
              <Card
                label="save-editor-heading"
                title={<span className="font-mono">{open.key}</span>}
                aside={env === 'test' ? 'JSON' : <Chip>Read-only</Chip>}
              >
                <JsonEditor
                  key={open.saveId}
                  label={`Contents of the save ${open.key}`}
                  value={open.data}
                  readOnly={env === 'prod'}
                  rows={6}
                  note={
                    env === 'test'
                      ? 'The game picks up a change the next time it loads this save.'
                      : 'To fix a player’s live progress, change it from your game, for example with a support code.'
                  }
                  onSave={async (data) => {
                    await apiSend('PUT', `${base}/saves/${open.saveId}`, { data });
                    changed();
                  }}
                  onDelete={async () => {
                    await apiSend('DELETE', `${base}/saves/${open.saveId}`);
                    setOpenKey(null);
                    changed();
                  }}
                  deleteLabel="Delete save"
                />
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// --- Game data ---------------------------------------------------------------------------

function NewDocument({
  base,
  collection,
  onMade,
}: {
  base: string;
  collection: string;
  onMade: (document: FullDocument) => void;
}) {
  const [id, setId] = useState('');
  const [text, setText] = useState('{\n  \n}');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function make(event: FormEvent) {
    event.preventDefault();
    const parsed = parseJson(text);
    if (!id.trim()) return setError('Give the document an id.');
    if (!parsed.ok) return setError(parsed.message);
    setBusy(true);
    setError(null);
    try {
      onMade(
        await apiSend<FullDocument>('POST', `${base}/collections/${collection}/documents`, {
          id: id.trim(),
          data: parsed.value,
        }),
      );
    } catch (failed) {
      setError(failure(failed));
      setBusy(false);
    }
  }

  return (
    <Card label="new-document-heading" title={`New document in ${collection}`}>
      <form onSubmit={make} className="flex flex-col gap-3">
        {error && <Alert tone="error">{error}</Alert>}
        <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-muted">
          Id
          <input
            value={id}
            onChange={(event) => setId(event.target.value)}
            className={`${fieldClass} font-mono`}
          />
        </label>
        <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-muted">
          Contents (JSON)
          <textarea
            rows={6}
            value={text}
            spellCheck={false}
            onChange={(event) => setText(event.target.value)}
            className={monoArea}
          />
        </label>
        <p className="text-sm text-muted">
          Made here, it’s shared with every player; they can read it but not change it.
        </p>
        <div>
          <Button type="submit" disabled={busy}>
            {busy ? 'Making…' : 'Make document'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function GameData({
  project,
  env,
  summary,
  refresh,
  onChanged,
}: {
  project: Project;
  env: Env;
  summary: Summary;
  refresh: number;
  onChanged: () => void;
}) {
  const names = [...new Set(summary.collections.map((c) => c.name))];
  const [collection, setCollection] = useState<string | null>(names[0] ?? null);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [belongs, setBelongs] = useState<'' | 'game' | 'player'>('');
  const [offset, setOffset] = useState(0);
  const [openId, setOpenId] = useState<number | null>(null);
  const [making, setMaking] = useState(false);
  const [reload, setReload] = useState(0);
  const base = `/v1/dev/projects/${project.id}/data`;

  // A collection that's gone (cleared, last document deleted) falls back to the first one.
  const active = collection && names.includes(collection) ? collection : (names[0] ?? null);

  const params = new URLSearchParams({ env, offset: String(offset) });
  if (query) params.set('search', query);
  if (belongs) params.set('scope', belongs);
  const documents = useApi<{ documents: DocumentRow[]; total: number }>(
    active ? `${base}/collections/${active}?${params}` : null,
    reload + refresh,
  );
  const opened = useApi<FullDocument>(
    openId ? `${base}/documents/${openId}?env=${env}` : null,
    reload + refresh,
  );
  const changed = () => {
    setReload((n) => n + 1);
    onChanged();
  };

  if (!active) {
    return (
      <section className={`${cardClass} p-6`}>
        <h2 className="text-[15px] font-semibold">No game data yet</h2>
        <p className="mt-2 text-muted">
          Collections appear here once your game writes to one, for example{' '}
          <code className="font-mono text-[13px]">
            mogzo.data.collection('levels').set('level-1', …)
          </code>
          .
        </p>
      </section>
    );
  }

  return (
    <div className="flex flex-wrap items-start gap-5">
      <Card
        label="collections-heading"
        title="Collections"
        className="max-w-[340px] flex-[1_1_260px]"
      >
        <p className="text-sm text-muted">Your game makes these when it first writes to one.</p>
        <ul className="flex flex-col gap-1">
          {names.map((name) => {
            const counts = summary.collections.filter((c) => c.name === name);
            const words = counts
              .map(
                (c) =>
                  `${c.documents} ${c.scope === 'game' ? 'game-wide' : 'player'} ${c.documents === 1 ? 'document' : 'documents'}`,
              )
              .join(', ');
            return (
              <li key={name}>
                <button
                  type="button"
                  aria-pressed={name === active}
                  onClick={() => {
                    setCollection(name);
                    setOpenId(null);
                    setOffset(0);
                    setMaking(false);
                  }}
                  className={`flex w-full flex-col gap-0.5 rounded-[6px] border px-3 py-2.5 text-left ${
                    name === collection
                      ? 'border-brand bg-cream'
                      : 'border-transparent hover:border-line'
                  }`}
                >
                  <span className="font-mono text-sm">{name}</span>
                  <span className="text-[13px] text-muted">{words}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </Card>

      <div className="flex min-w-0 flex-[999_1_440px] flex-col gap-5">
        <Card
          label="documents-heading"
          title={<span className="font-mono">{active}</span>}
          aside={
            env === 'test' && (
              <Button
                variant="brand"
                onClick={() => {
                  setMaking(true);
                  setOpenId(null);
                }}
              >
                New document
              </Button>
            )
          }
        >
          <form
            className="flex flex-wrap gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              setQuery(search.trim());
              setOffset(0);
            }}
          >
            <label className="flex flex-[1_1_220px] flex-col gap-1.5 text-[13px] font-semibold text-muted">
              Find by id
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className={fieldClass}
              />
            </label>
            <label className="flex flex-[0_1_200px] flex-col gap-1.5 text-[13px] font-semibold text-muted">
              Belongs to
              <select
                value={belongs}
                onChange={(event) => {
                  setBelongs(event.target.value as typeof belongs);
                  setOffset(0);
                }}
                className={fieldClass}
              >
                <option value="">Everything</option>
                <option value="game">The whole game</option>
                <option value="player">One player</option>
              </select>
            </label>
          </form>
          {documents.status === 'loading' && <Loading />}
          {documents.status === 'error' && <ErrorNotice error={documents.error} />}
          {documents.status === 'ready' && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="text-left text-xs text-muted">
                      <th className="border-b border-divider px-3 py-2.5">Id</th>
                      <th className="border-b border-divider px-3 py-2.5">Belongs to</th>
                      <th className="border-b border-divider px-3 py-2.5">Size</th>
                      <th className="border-b border-divider px-3 py-2.5">Changed</th>
                      <th className="border-b border-divider px-3 py-2.5">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.data.documents.map((doc) => (
                      <tr
                        key={doc.documentId}
                        className={doc.documentId === openId ? 'bg-cream' : ''}
                      >
                        <td className="border-b border-divider px-3 py-3 font-mono text-[13px]">
                          {doc.id}
                        </td>
                        <td className="border-b border-divider px-3 py-3">
                          <Chip>
                            {doc.scope === 'game'
                              ? 'Whole game'
                              : (doc.player?.name ?? `Player ${doc.player?.playerId ?? ''}`)}
                          </Chip>
                        </td>
                        <td className="border-b border-divider px-3 py-3 text-muted">
                          {size(doc.bytes)}
                        </td>
                        <td className="border-b border-divider px-3 py-3 text-muted">
                          {timeAgo(doc.updatedAt)}
                        </td>
                        <td className="border-b border-divider px-3 py-3 text-right">
                          <button
                            type="button"
                            className={linkClass}
                            aria-label={`${env === 'test' ? 'Open' : 'View'} ${doc.id}`}
                            onClick={() => {
                              setOpenId(doc.documentId);
                              setMaking(false);
                            }}
                          >
                            {env === 'test' ? 'Open' : 'View'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-muted">
                {documents.data.total === 0
                  ? 'Nothing matches.'
                  : `Showing ${offset + 1}–${offset + documents.data.documents.length} of ${documents.data.total}`}
                {offset + documents.data.documents.length < documents.data.total && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      className={linkClass}
                      onClick={() => setOffset(offset + 50)}
                    >
                      Show more
                    </button>
                  </>
                )}
              </p>
            </>
          )}
        </Card>

        {making && env === 'test' && (
          <NewDocument
            base={base}
            collection={active}
            onMade={(doc) => {
              setMaking(false);
              setOpenId(doc.documentId);
              changed();
            }}
          />
        )}
        {openId && opened.status === 'error' && <ErrorNotice error={opened.error} />}
        {openId && opened.status === 'ready' && (
          <Card
            label="document-editor-heading"
            title={<span className="font-mono">{opened.data.id}</span>}
            aside={
              env === 'test' ? (
                `${opened.data.scope === 'game' ? 'Whole game' : 'One player'} · JSON`
              ) : (
                <Chip>Read-only</Chip>
              )
            }
          >
            <JsonEditor
              key={opened.data.documentId}
              label={`Contents of the document ${opened.data.id}`}
              value={opened.data.data}
              readOnly={env === 'prod'}
              rows={9}
              note={
                opened.data.scope === 'game'
                  ? `Shared by every player${opened.data.player ? `; made by ${opened.data.player.name ?? `player ${opened.data.player.playerId}`}` : '; made here, so players can’t change it'}.`
                  : 'Belongs to one player.'
              }
              onSave={async (data) => {
                await apiSend('PUT', `${base}/documents/${opened.data.documentId}`, { data });
                changed();
              }}
              onDelete={async () => {
                await apiSend('DELETE', `${base}/documents/${opened.data.documentId}`);
                setOpenId(null);
                changed();
              }}
              deleteLabel="Delete document"
            />
          </Card>
        )}
      </div>
    </div>
  );
}

// --- Remote config -----------------------------------------------------------------------

function RemoteConfig({ project, env, refresh }: { project: Project; env: Env; refresh: number }) {
  const [reload, setReload] = useState(0);
  const [copying, setCopying] = useState<'idle' | 'confirm' | 'busy' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  const path = `/v1/dev/projects/${project.id}/config`;
  const config = useApi<ConfigState>(`${path}?env=${env}`, reload + refresh);

  async function copy() {
    setCopying('busy');
    setError(null);
    try {
      await apiSend('POST', `${path}/copy-to-live`);
      setCopying('done');
    } catch (failed) {
      setError(failure(failed));
      setCopying('idle');
    }
  }

  return (
    <div className="flex flex-wrap items-start gap-5">
      <div className="flex min-w-0 flex-[999_1_480px] flex-col gap-3">
        {config.status === 'loading' && <Loading />}
        {config.status === 'error' && <ErrorNotice error={config.error} />}
        {config.status === 'ready' && (
          <Card
            label="config-heading"
            title={env === 'test' ? 'Test config' : 'Live config'}
            aside={`${config.data.updatedAt ? `Changed ${timeAgo(config.data.updatedAt)}` : 'Not set yet'} · ${size(config.data.bytes)} of ${size(config.data.limit)}`}
          >
            <p className="text-muted">
              {env === 'test'
                ? 'Settings your game reads when it starts, so you can tune it without a new build.'
                : 'What your published game reads. Changes reach players the next time they start the game.'}
            </p>
            {error && <Alert tone="error">{error}</Alert>}
            {copying === 'done' && (
              <Alert tone="success">Live config now matches test config.</Alert>
            )}
            <JsonEditor
              key={env}
              label={env === 'test' ? 'Test config, as JSON' : 'Live config, as JSON'}
              value={config.data.config}
              readOnly={false}
              rows={12}
              saveLabel={env === 'test' ? 'Save test config' : 'Save live config'}
              onSave={async (value) => {
                if (!value || typeof value !== 'object' || Array.isArray(value)) {
                  throw new ApiError(
                    400,
                    'bad_request',
                    'Config is a JSON object, like { "speed": 2 }.',
                  );
                }
                await apiSend('PUT', `${path}?env=${env}`, { config: value });
                setReload((n) => n + 1);
              }}
            />
            {env === 'test' &&
              (copying === 'confirm' || copying === 'busy' ? (
                <div className="flex flex-wrap items-center gap-2.5 rounded-[6px] bg-cream px-4 py-3">
                  <span className="flex-[1_1_240px] text-sm font-semibold">
                    Replace live config with the saved test config? Players get it the next time
                    they start the game.
                  </span>
                  <Button variant="brand" onClick={copy} disabled={copying === 'busy'}>
                    {copying === 'busy' ? 'Copying…' : 'Yes, copy to live'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setCopying('idle')}
                    disabled={copying === 'busy'}
                  >
                    Not now
                  </Button>
                </div>
              ) : (
                <div>
                  <Button variant="brand" onClick={() => setCopying('confirm')}>
                    Copy to live config…
                  </Button>
                </div>
              ))}
          </Card>
        )}
      </div>
      <aside
        aria-labelledby="config-howto"
        className={`${cardClass} flex max-w-[360px] flex-[1_1_280px] flex-col gap-3 p-5`}
      >
        <h2 id="config-howto" className="text-[15px] font-semibold">
          In your game
        </h2>
        <pre className="rounded-[6px] border border-line bg-field px-3.5 py-3 font-mono text-[13px] leading-relaxed whitespace-pre-wrap">
          {'const config = await mogzo.config.get();\nspawnEvery = config.spawnEvery ?? 1;'}
        </pre>
        <ul className="flex list-disc flex-col gap-2 pl-5 text-muted">
          <li>Games get new values the next time they start.</li>
          <li>Test and live config are separate. Try a change here first, then copy it to live.</li>
          <li>Live config is the only live data you can change here.</li>
        </ul>
      </aside>
    </div>
  );
}

// --- Clear test data ---------------------------------------------------------------------

function ClearTestData({
  project,
  summary,
  onCleared,
}: {
  project: Project;
  summary: Summary;
  onCleared: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState({
    saves: true,
    data: true,
    scores: true,
    achievements: true,
    config: false,
  });
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function clear(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await apiSend<{
        saves: number;
        documents: number;
        scores: number;
        achievements: number;
        config: boolean;
      }>('POST', `/v1/dev/projects/${project.id}/data/clear`, { ...choice, confirm });
      setOpen(false);
      setConfirm('');
      onCleared(
        `Cleared ${result.saves} ${result.saves === 1 ? 'save' : 'saves'} and ${result.documents} ${result.documents === 1 ? 'document' : 'documents'}${result.scores ? `, ${result.scores} ${result.scores === 1 ? 'score' : 'scores'}` : ''}${result.achievements ? `, ${result.achievements} achievement ${result.achievements === 1 ? 'unlock or progress' : 'unlocks and progress'}` : ''}${result.config ? ', and test config' : ''}.`,
      );
    } catch (failed) {
      setError(failure(failed));
    } finally {
      setBusy(false);
    }
  }

  const option = (key: keyof typeof choice, title: string, detail: string) => (
    <label className="flex min-h-11 items-center gap-3 rounded-[6px] border border-line bg-field px-3.5">
      <input
        type="checkbox"
        checked={choice[key]}
        onChange={(event) => setChoice({ ...choice, [key]: event.target.checked })}
        className="size-[18px] accent-ink"
      />
      <span>
        <strong>{title}</strong> <span className="text-muted">· {detail}</span>
      </span>
    </label>
  );

  return (
    <section
      aria-labelledby="clear-heading"
      className={`${cardClass} flex flex-wrap items-center justify-between gap-4 px-6 py-5`}
    >
      <div className="flex-[1_1_360px]">
        <h2 id="clear-heading" className="text-[15px] font-semibold">
          Start testing fresh
        </h2>
        <p className="mt-1.5 text-muted">
          Delete test saves, game data, leaderboard scores and achievement unlocks. Live data is
          never touched.
        </p>
      </div>
      <Button variant="danger" onClick={() => setOpen(true)}>
        Clear test data…
      </Button>
      {open && (
        <div className="fixed inset-0 z-10 flex items-start justify-center overflow-y-auto bg-[var(--overlay)] px-4 py-24">
          <form
            role="dialog"
            aria-modal="true"
            aria-labelledby="clear-title"
            onSubmit={clear}
            className="flex w-full max-w-[520px] flex-col gap-4 rounded-[20px] border border-line bg-card p-7"
          >
            <h2 id="clear-title" className="text-[22px] font-semibold">
              Clear {project.name}’s test data?
            </h2>
            <p className="leading-relaxed text-muted">
              Your testers start fresh: their progress and the game’s test documents are deleted.
              Live data is never touched. This can’t be undone.
            </p>
            {error && <Alert tone="error">{error}</Alert>}
            <fieldset className="flex flex-col gap-2.5">
              <legend className="mb-2.5 text-[13px] font-semibold text-muted">
                What to delete
              </legend>
              {option(
                'saves',
                'Saves',
                `saves from ${summary.playersWithSaves} ${summary.playersWithSaves === 1 ? 'player' : 'players'}`,
              )}
              {option(
                'data',
                'Game data',
                `${summary.documents} ${summary.documents === 1 ? 'document' : 'documents'}`,
              )}
              {option('scores', 'Leaderboard scores', 'every board, every period')}
              {option('achievements', 'Achievement unlocks', 'and progress; the achievements stay')}
              {option('config', 'Test config', 'back to empty')}
            </fieldset>
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-muted">
              <span>
                To confirm, type the game’s address,{' '}
                <span className="font-mono text-ink">{project.slug}</span>
              </span>
              <input
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="off"
                className={`${fieldClass} font-mono`}
              />
            </label>
            <div className="flex flex-wrap justify-end gap-2.5">
              <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>
                Cancel
              </Button>
              <button
                type="submit"
                disabled={
                  busy ||
                  confirm.trim().toLowerCase() !== project.slug.toLowerCase() ||
                  !(choice.saves || choice.data || choice.config)
                }
                className="mz-btn mz-btn-danger"
              >
                {busy ? 'Clearing…' : 'Clear test data'}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

// --- Clear live data (M23) ---------------------------------------------------------------

interface LiveData {
  scores: number;
  saves: number;
  achievements: number;
  clearedAt: string | null;
}

/** The one-time start-fresh for a live game, before MOGZO opens to players. */
function ClearLiveData({
  project,
  refresh,
  onCleared,
}: {
  project: Project;
  refresh: number;
  onCleared: (message: string) => void;
}) {
  const live = useApi<LiveData>(`/v1/dev/projects/${project.id}/live-data`, refresh);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (live.status !== 'ready') return null;
  const { scores, saves, achievements, clearedAt } = live.data;
  const count = (n: number, one: string, many: string) =>
    `${n.toLocaleString('en')} ${n === 1 ? one : many}`;

  async function clear(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiSend('POST', `/v1/dev/projects/${project.id}/live-data/clear`, { confirm });
      setOpen(false);
      onCleared('Live data cleared. The game starts fresh for MOGZO’s launch.');
    } catch (failed) {
      setError(failure(failed));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      aria-labelledby="clear-live-heading"
      className={`${cardClass} flex flex-wrap items-center justify-between gap-4 px-6 py-5`}
    >
      <div className="flex-[1_1_360px]">
        <h2 id="clear-live-heading" className="text-[15px] font-semibold">
          Clear live data
        </h2>
        <p className="mt-1.5 text-muted">
          {clearedAt
            ? `Live data was cleared on ${new Date(clearedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}.`
            : 'Start the game fresh for MOGZO’s launch: deletes live scores, saves, achievements players have and players’ game data. Leaderboards, achievements, remote config and documents you made stay. You can do this once, until MOGZO opens to players.'}
        </p>
      </div>
      {!clearedAt && (
        <Button variant="danger" onClick={() => setOpen(true)}>
          Clear live data…
        </Button>
      )}
      {open && (
        <div className="fixed inset-0 z-10 flex items-start justify-center overflow-y-auto bg-[var(--overlay)] px-4 py-24">
          <form
            role="dialog"
            aria-modal="true"
            aria-labelledby="clear-live-title"
            onSubmit={clear}
            className="flex w-full max-w-[520px] flex-col gap-4 rounded-[20px] border border-line bg-card p-7"
          >
            <h2 id="clear-live-title" className="text-[22px] font-semibold">
              Clear {project.name}’s live data?
            </h2>
            <p className="leading-relaxed text-muted">
              This deletes {count(scores, 'score', 'scores')}, {count(saves, 'save', 'saves')} and{' '}
              {count(achievements, 'achievement players have', 'achievements players have')}, with
              players’ game data. It can’t be undone, and you can only do it once.
            </p>
            {error && <Alert tone="error">{error}</Alert>}
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-muted">
              <span>
                To confirm, type the game’s address,{' '}
                <span className="font-mono text-ink">{project.slug}</span>
              </span>
              <input
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="off"
                className={`${fieldClass} font-mono`}
              />
            </label>
            <div className="flex flex-wrap justify-end gap-2.5">
              <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>
                Cancel
              </Button>
              <button
                type="submit"
                disabled={busy || confirm.trim().toLowerCase() !== project.slug.toLowerCase()}
                className="mz-btn mz-btn-danger"
              >
                {busy ? 'Clearing…' : 'Clear live data'}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

// --- The page ----------------------------------------------------------------------------

const TABS: [Tab, string][] = [
  ['saves', 'Saves'],
  ['data', 'Game data'],
  ['leaderboards', 'Leaderboards'],
  ['achievements', 'Achievements'],
  ['config', 'Remote config'],
  ['matches', 'Matches'],
  ['attacks', 'Attacks'],
  ['rooms', 'Rooms'],
];

const KIND_ICONS: Record<Tab, LucideIcon> = {
  saves: Users,
  data: Database,
  leaderboards: Trophy,
  achievements: CircleCheck,
  config: Settings,
  matches: Swords,
  attacks: Castle,
  rooms: Radio,
};

/**
 * The Data tab (M16–M18, laid out in M25): a game's saves, game data, leaderboards,
 * achievements, remote config, matches (M32), attacks on bases (M34) and realtime rooms (M35),
 * test and live.
 */
export function ProjectData() {
  const { project: current } = useProject();
  const [params, setParams] = useSearchParams();
  const env: Env = params.get('env') === 'prod' ? 'prod' : 'test';
  const tab: Tab = (TABS.find(([key]) => key === params.get('tab'))?.[0] ?? 'saves') as Tab;
  const [reload, setReload] = useState(0);
  const [cleared, setCleared] = useState<string | null>(null);
  const summary = useApi<Summary>(`/v1/dev/projects/${current.id}/data?env=${env}`, reload);
  const go = (next: { env?: Env; tab?: Tab }) => {
    const query = new URLSearchParams();
    const nextEnv = next.env ?? env;
    const nextTab = next.tab ?? tab;
    if (nextEnv === 'prod') query.set('env', 'prod');
    if (nextTab !== 'saves') query.set('tab', nextTab);
    setParams(query);
  };
  const changed = () => setReload((n) => n + 1);

  return (
    <>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <SegmentedControl
          label="Which data"
          value={env}
          onChange={(value) => go({ env: value })}
          items={[
            { id: 'test', label: 'Test' },
            { id: 'prod', label: 'Live' },
          ]}
        />
        <p className="m-0 flex-[1_1_320px] text-[13px] text-muted">
          {env === 'test'
            ? 'What your test community plays with. Change anything here; real players never see it.'
            : 'Your published game’s real players. You can look, not change, and players show as anonymous ids.'}
        </p>
        <Badge tone={env === 'test' ? 'info' : 'success'}>
          {env === 'test' ? 'Test data' : 'Live data'}
        </Badge>
      </div>

      {summary.status === 'error' && <ErrorNotice error={summary.error} />}
      {/* The Matches and Attacks pages show their own numbers instead. */}
      {summary.status === 'ready' && tab !== 'matches' && tab !== 'attacks' && tab !== 'rooms' && (
        <dl className="m-0 grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4">
          {[
            [
              'Storage used',
              `${size(summary.data.storageBytes)}`,
              `of ${size(summary.data.storageLimit)}`,
            ],
            ['Players with saves', summary.data.playersWithSaves.toLocaleString('en'), ''],
            ['Game data documents', summary.data.documents.toLocaleString('en'), ''],
          ].map(([label, value, extra]) => (
            <div key={label} className="mz-panel px-5 py-4">
              <dt className="text-[13px] text-muted">{label}</dt>
              <dd className="mz-text-title-lg m-0 mt-1 tabular-nums">
                {value}{' '}
                {extra && <span className="text-[13px] font-normal text-muted">{extra}</span>}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {cleared && <Alert tone="success">{cleared}</Alert>}

      <div className="flex flex-wrap items-start gap-6">
        <nav aria-label="Kinds of data" className="flex flex-[0_0_200px] flex-col gap-0.5">
          {TABS.map(([key, label]) => {
            const Icon = KIND_ICONS[key];
            return (
              <button
                key={key}
                type="button"
                className="mz-nav-item border-0 bg-transparent text-left"
                aria-current={tab === key ? 'page' : undefined}
                onClick={() => go({ tab: key })}
              >
                <Icon size={16} strokeWidth={1.75} aria-hidden="true" className="mz-icon" />
                <span>{label}</span>
              </button>
            );
          })}
        </nav>

        <div className="flex min-w-0 flex-[999_1_480px] flex-col gap-6">
          {tab === 'saves' && (
            <Saves
              key={`saves-${env}`}
              project={current}
              env={env}
              refresh={reload}
              onChanged={changed}
            />
          )}
          {tab === 'data' &&
            (summary.status === 'ready' ? (
              <GameData
                key={`data-${env}`}
                project={current}
                env={env}
                summary={summary.data}
                refresh={reload}
                onChanged={changed}
              />
            ) : (
              <Loading />
            ))}
          {tab === 'leaderboards' && (
            <Leaderboards
              key={`leaderboards-${env}`}
              project={current}
              env={env}
              refresh={reload}
            />
          )}
          {tab === 'achievements' && (
            <Achievements
              key={`achievements-${env}`}
              project={current}
              env={env}
              refresh={reload}
            />
          )}
          {tab === 'config' && (
            <RemoteConfig key={`config-${env}`} project={current} env={env} refresh={reload} />
          )}
          {tab === 'matches' && <Matches key={`matches-${env}`} project={current} env={env} />}
          {tab === 'attacks' && <Attacks key={`attacks-${env}`} project={current} env={env} />}
          {tab === 'rooms' && <Rooms key={`rooms-${env}`} project={current} env={env} />}

          {env === 'test' && summary.status === 'ready' && (
            <ClearTestData
              project={current}
              summary={summary.data}
              onCleared={(message) => {
                setCleared(message);
                changed();
              }}
            />
          )}
          {env === 'prod' && current.liveVersion && (
            <ClearLiveData
              project={current}
              refresh={reload}
              onCleared={(message) => {
                setCleared(message);
                changed();
              }}
            />
          )}
        </div>
      </div>
    </>
  );
}
